import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { type UIIconName, UI_REGISTRY } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export type TextInputSize = 'sm' | 'md' | 'lg';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  readonly size?: TextInputSize;
  /** Leading icon — from @bravemove/design-tokens/icons UI family. */
  readonly icon?: UIIconName;
  /** Trailing slot — usually an icon button or `<kbd>` shortcut hint. */
  readonly trailing?: ReactNode;
  readonly invalid?: boolean;
  /** Use when the trailing slot provides an accessible search-clear button. */
  readonly hideNativeSearchClear?: boolean;
}

const SIZE: Record<TextInputSize, string> = {
  sm: 'h-[34px] text-[12.5px]',
  md: 'h-10 text-[13.5px]',
  lg: 'h-12 text-[15px]',
};

const PADDING: Record<TextInputSize, { withIcon: string; bare: string }> = {
  sm: { withIcon: 'ps-9 pe-3', bare: 'px-3' },
  md: { withIcon: 'ps-10 pe-3.5', bare: 'px-3.5' },
  lg: { withIcon: 'ps-12 pe-4', bare: 'px-4' },
};

const ICON_INSET: Record<TextInputSize, string> = {
  sm: 'start-3',
  md: 'start-3.5',
  lg: 'start-4',
};

const ICON_SIZE: Record<TextInputSize, number> = {
  sm: 16,
  md: 18,
  lg: 20,
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  {
    size = 'md',
    icon,
    trailing,
    invalid,
    className,
    type = 'text',
    disabled,
    hideNativeSearchClear = false,
    ...rest
  },
  ref,
) {
  const Icon = icon ? UI_REGISTRY[icon] : null;
  const hasTrailing = !!trailing;
  return (
    <div className={cn('relative inline-flex items-stretch w-full', disabled && 'opacity-45')}>
      {Icon ? (
        <span
          className={cn(
            'pointer-events-none absolute inset-y-0 flex items-center text-ink-3',
            ICON_INSET[size],
          )}
          aria-hidden="true"
        >
          <Icon size={ICON_SIZE[size]} color="currentColor" />
        </span>
      ) : null}
      <input
        ref={ref}
        type={type}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(
          'w-full min-w-0 rounded-lg bg-surface text-ink [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:text-base',
          'border border-ink-3/80 placeholder:text-ink-3',
          'transition-[box-shadow,border-color] duration-(--duration-tap) ease-(--ease-out) motion-reduce:transition-none',
          'focus:outline-2 focus:outline-offset-2 focus:outline-accent-text focus:border-accent-text',
          'disabled:cursor-not-allowed',
          invalid && 'border-danger focus:border-danger focus:ring-danger/25',
          SIZE[size],
          Icon ? PADDING[size].withIcon : PADDING[size].bare,
          hasTrailing && 'pe-12',
          // Browsers add their own clear button to search inputs. A supplied
          // trailing control takes its place, so there is only one visible action.
          type === 'search' &&
            hideNativeSearchClear &&
            '[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-cancel-button]:hidden',
          className,
        )}
        {...rest}
      />
      {trailing ? (
        <span className="absolute inset-y-0 end-2 flex items-center text-ink-3">{trailing}</span>
      ) : null}
    </div>
  );
});
